import React from 'react'
import { Bell, BellOff, Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { usePushSubscription } from '@/modules/pwa/hooks/usePushSubscription'
import { useCommunityThreadNotifications } from '../hooks/useCommunityThreadNotifications'
import type { CommunityThreadContextType } from '../services/communityThreadNotificationsService'

type CommunityThreadNotificationsToggleProps = {
  contextType: CommunityThreadContextType
  contextId: number
  active: boolean
}

export function CommunityThreadNotificationsToggle({
  contextType,
  contextId,
  active,
}: CommunityThreadNotificationsToggleProps) {
  const { add: toast } = useToast()
  const push = usePushSubscription()
  const { enabled, loading, saving, setEnabled } = useCommunityThreadNotifications({
    contextType,
    contextId,
    active,
  })
  const [activatingPush, setActivatingPush] = React.useState(false)

  const busy = loading || saving || activatingPush || push.loading

  async function handleToggle() {
    if (busy) return

    // Ativar: primeiro garante push global (permissao + subscription)
    if (!enabled) {
      if (push.status === 'unsupported') {
        toast({
          title: 'Notificacoes',
          description: 'Seu navegador nao suporta Web Push neste dispositivo.',
          variant: 'error',
        })
        return
      }

      if (push.status !== 'subscribed') {
        setActivatingPush(true)
        try {
          const ok = await push.enable()
          if (!ok) {
            toast({
              title: 'Notificacoes',
              description:
                'Nao foi possivel ativar as notificacoes neste dispositivo. Verifique as permissoes e tente novamente.',
              variant: 'error',
            })
            return
          }
        } finally {
          setActivatingPush(false)
        }
      }

      const res = await setEnabled(true)
      if (res === true) {
        toast({
          title: 'Notificacoes',
          description: 'Notificacoes ativadas para esta conversa.',
          variant: 'success',
          soft: true,
        })
      } else {
        toast({
          title: 'Notificacoes',
          description: 'Nao foi possivel ativar para esta conversa. Tente novamente.',
          variant: 'error',
        })
      }
      return
    }

    // Desativar: apenas esta conversa (nao mexe no push global)
    const res = await setEnabled(false)
    if (res === null) {
      toast({
        title: 'Notificacoes',
        description: 'Nao foi possivel desativar para esta conversa. Tente novamente.',
        variant: 'error',
      })
      return
    }

    if (res === false) {
      toast({
        title: 'Notificacoes',
        description: 'Notificacoes desativadas para esta conversa.',
        variant: 'info',
        soft: true,
      })
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      className="h-8 px-2 text-xs"
      onClick={() => void handleToggle()}
      disabled={busy}
      title={enabled ? 'Desativar notificacoes desta conversa' : 'Ativar notificacoes desta conversa'}
      aria-label={enabled ? 'Desativar notificacoes desta conversa' : 'Ativar notificacoes desta conversa'}
    >
      {busy ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : enabled ? (
        <BellOff className="mr-2 h-4 w-4" />
      ) : (
        <Bell className="mr-2 h-4 w-4" />
      )}
      {enabled ? 'Desativar' : 'Ativar'}
    </Button>
  )
}
